import { Component } from "react";
import { ArrowCounterClockwise, House, Warning } from "@phosphor-icons/react";

/**
 * Top-level safety net. Wraps each app shell (MobileShell, DesktopShell,
 * SiteLayout) so a render error inside any nested route doesn't
 * white-screen the whole app.
 *
 * Design choices:
 *   * Must be a class component — React's componentDidCatch hook has no
 *     function-component equivalent.
 *   * Fallback is calm and branded (no red "ERROR!" flags) so parents
 *     and teachers aren't unsettled. Copy is action-oriented: retry,
 *     go home, refresh.
 *   * `scope` label ("parent", "teacher", "admin", "cms", "public")
 *     lets the fallback customise the "home" link per role.
 *   * `key` (via resetKey prop passthrough) allows the parent to force
 *     a remount after a successful recovery elsewhere — we accept the
 *     simplest path: users retry via the button, which calls setState
 *     to clear the error.
 *
 * We intentionally do NOT ship this to a crash-reporting service here
 * — that integration belongs in a separate module with its own env var.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, stamp: 0 };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log to console (plain-text) so operators can pull from the browser
    // network pane / service-worker logs. Resist the urge to POST here —
    // a broken API path could itself be the reason we're in this branch.
    // eslint-disable-next-line no-console
    console.error(
      "[ErrorBoundary]",
      this.props.scope || "root",
      error?.message || error,
      info?.componentStack,
    );
  }

  reset = () => {
    this.setState({ error: null, stamp: this.state.stamp + 1 });
  };

  render() {
    if (!this.state.error) return this.props.children;

    const scope = this.props.scope || "root";
    const home = HOME_FOR_SCOPE[scope] || "/";

    return (
      <div
        role="alert"
        data-testid="error-boundary-fallback"
        data-scope={scope}
        className="flex min-h-[60vh] items-center justify-center px-6 py-12"
      >
        <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-[0_2px_12px_rgba(20,22,26,0.04)]">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C6851E]/10 text-[#C6851E]">
            <Warning size={22} weight="duotone" />
          </div>
          <h2 className="mt-4 font-heading text-xl font-semibold tracking-tight text-neutral-900">
            Something went off-track
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-neutral-600">
            This page hit an unexpected error. Nothing has been lost — you
            can try again or head home.
          </p>

          <div className="mt-6 flex flex-col items-stretch gap-2 sm:flex-row sm:justify-center">
            <button
              type="button"
              data-testid="error-boundary-retry"
              onClick={this.reset}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-hover"
            >
              <ArrowCounterClockwise size={14} weight="bold" />
              Try again
            </button>
            <a
              data-testid="error-boundary-home"
              href={home}
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-50"
            >
              <House size={14} weight="bold" />
              Go home
            </a>
            <button
              type="button"
              data-testid="error-boundary-refresh"
              onClick={() => {
                if (typeof window !== "undefined") window.location.reload();
              }}
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-50"
            >
              Refresh
            </button>
          </div>

          {process.env.NODE_ENV !== "production" && this.state.error?.message && (
            <pre className="mt-5 max-h-40 overflow-auto rounded-xl bg-neutral-50 p-3 text-left text-[11px] leading-relaxed text-neutral-500">
              {this.state.error.message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

const HOME_FOR_SCOPE = {
  parent:  "/parent",
  teacher: "/teacher",
  admin:   "/admin",
  cms:     "/cms",
  public:  "/",
  root:    "/",
};
